/**
 * 5D (DEC-49, criteria F1 to F3): Leadership's structured sections, the same on the page and in the one-page PDF.
 *
 * Each view opens with up to three answer cards built on the shared opening lines (leadership-lines.ts): a small
 * question, the answer as one figure or a short statement, a state chip in informative words (never pass or fail; the
 * off-track tone only for a committed plan off track), the line saying what it means, and where the figure comes
 * from. The Summary ends with network-wide focus points generated from the data, in place of "Decisions waiting".
 *
 * The rules are the opening lines' own (networkAttentionKind, firmAttentionKind, weakestRisks),
 * so a card never says something its line does not.
 */
import { BTI_WAVE1_EVALUATION, BTI_WAVE2_DUE, BTI_WAVE2_ITEMS, btiPositionCounts, btiVerdictCounts } from './bti-evaluation.js';
import { findingCounts, GOALS, isOpenFinding, longDate, PROGRAM_NAME, rankedDots } from './leadership.js';
import { assessmentLines, btiLines, daysToWave2, firmAttentionKind, belowHalf, GOAL_SIZE, joinNames, memberFirms, networkAttentionKind, networkLines, ordinalOf, summaryLines, unratedNote, weakestRisks, } from './leadership-lines.js';
const pct = (v) => `${Math.round(v)}%`;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** Where Leadership's figures come from, in one wording for the page and the PDF (F2). */
export const SOURCE = {
    cdw: 'CDW 2026 assessment and National IT reviews',
    tracker: 'CDW assessment tracker',
    register: 'CDW risk registers, with progress as each firm reports it',
    acceptances: "firms' own risk acceptance records",
    plans: 'action plans, as each firm reports them',
    btiReview: "BTI's review of the July 2025 submission",
    btiWave2: 'BTI Resilience Policy Wave 2 requirements',
    nationalIt: "National IT's own positions",
    goal: 'the network goal National IT set in 2026',
    survey: "March 2025 survey (firms' own answers)",
    snapshots: "the portal's stored quarterly figures",
    mapping: 'BTI requirements matched to the baseline controls',
};
/**
 * How many of a list CDW rated high or very high, as a state chip: the one rule for the findings and risk acceptances
 * (5E NEXT PASS 2, E7). SFH-05: an item CDW never rated is not a low rating, so rated, not rated and higher are counted
 * apart.
 */
export function higherState(list) {
    const unrated = list.filter((x) => x.higher !== true && x.higher !== false).length;
    if (unrated === list.length)
        return { label: 'Not rated by CDW', tone: 'neutral' };
    const n = list.filter((x) => x.higher === true).length;
    const rated = n ? `${n} rated high or very high by CDW` : 'None rated high or very high';
    return { label: unrated ? `${rated}; ${unrated} not rated by CDW` : rated, tone: 'neutral' };
}
/** The network's figures: the median of the firms with CDW results, said with how many. */
export const networkSourceText = (n) => 
// UX-06 (5E pre-gate): below five firms there is no median to name (never "median of 0 firms").
n.firmsWithResults >= 5 ? `${SOURCE.cdw}, median of ${plural(n.firmsWithResults, 'firm')}` : `${SOURCE.cdw}, a median once five or more firms have results`;
/** One firm's figures, by where they rest. */
export function firmSourceText(f) {
    // Not SOURCE.cdw word for word: a firm member's opening never names National IT (5C L1), so a firm's own figures say
    // the accepted reviews without it.
    if (f.source === 'cdw')
        return `${f.firmName}'s CDW 2026 assessment, with accepted reviews where newer`;
    if (f.source === 'self')
        return `${f.firmName}'s own 2026 rating, while it waits for CDW`;
    if (f.march2025Baselines)
        return `${f.firmName}'s March 2025 survey answers`;
    // F2 (NEXT PASS 4): the source by name, where the firm's figures will come from; the state (no results yet) is said
    // on the tiles and cards themselves, never as a source.
    return `${f.firmName}'s CDW 2026 assessment and its own ratings in this portal`;
}
/**
 * A headline tile's source in a few words (F2), the same on the page and the PDF: the network's medians from the CDW
 * firms, a firm's figures from what they rest on, plans as firms report them, higher risks from the CDW register.
 */
export function tileSource(measure, d) {
    const f = d.firm;
    if (measure === 'plans')
        return f ? `${f.firmName}'s action plans` : "Firms' own action plans";
    if (measure === 'higherRisks')
        return f ? `${f.firmName}'s CDW risk register` : 'CDW risk registers';
    // PL-07 (5E pre-gate): SOURCE.cdw whole, never a shortened "CDW 2026".
    if (measure === 'firms')
        return SOURCE.cdw;
    if (!f)
        return networkSourceText(d.network);
    if (f.source === 'cdw')
        return SOURCE.cdw;
    if (f.source === 'self')
        return "The firm's own 2026 rating";
    // The survey asked no critical-priority question as such; the critical tile waits for a 2026 rating.
    // F2 (NEXT PASS 4): a source by name, never the state ("No 2026 rating yet" is said on the tile).
    return f.march2025Baselines && measure !== 'critical' ? 'March 2025 survey' : `${SOURCE.cdw}, or the firm's own 2026 rating`;
}
/** What "the typical firm" means (F5), said near the top of the network tab and in the PDF. */
export const typicalFirmNote = (n) => 
// 5E NEXT PASS 3 (E5): below five firms there is no median to name (never "the median of the 0 firms").
n.firmsWithResults >= 5
    ? `The typical firm is the median of the ${plural(n.firmsWithResults, 'firm')} with CDW results: half of them are above it and half below.`
    : 'The typical firm is the median of the firms with CDW results, shown once five or more firms have them.';
/** A share against a goal of 100%, in words: never a verdict. */
export function stateOf(v) {
    if (v === null || v === undefined)
        return { label: 'No results yet', tone: 'neutral' };
    if (Math.round(v) >= 100)
        return { label: 'All in place', tone: 'in_place' };
    if (Math.round(v) >= 50)
        return { label: 'More than half in place', tone: 'progress' };
    return { label: 'Less than half in place', tone: 'neutral' };
}
/** How far a share is from 100%. */
export function toGo(v) {
    if (v === null || v === undefined)
        return { label: 'No results yet', tone: 'neutral' };
    const left = Math.max(0, Math.round(100 - v));
    return left === 0 ? { label: 'Goal reached', tone: 'in_place' } : { label: `${plural(left, 'point')} to go`, tone: 'neutral' };
}
/** The days to Wave 2, as a chip: the one count (daysToWave2) every card uses. */
export function wave2Chip(asOf) {
    const d = daysToWave2(asOf);
    return { label: d > 0 ? `Due in ${plural(d, 'day')}` : d === 0 ? 'Due today' : 'Past its due date', tone: 'neutral' };
}
/**
 * A firm's share of the rated requirements as chips (SFH-01): while some of the goal's requirements are not rated, the
 * position chip says how many are, and the goal chip never says "Goal reached"; "All in place" and "Goal reached" only
 * when every requirement in the goal is rated.
 */
export function ratedChips(v, rated, size) {
    const note = v === null ? null : unratedNote(rated, size);
    if (!note)
        return { position: stateOf(v), goal: toGo(v) };
    const chip = { label: note, tone: 'neutral' };
    const goal = toGo(v);
    return { position: chip, goal: goal.tone === 'in_place' ? chip : goal };
}
const card = (id, question, answer, chip, line, source) => line === undefined ? [] : [{ id, question, answer, chip, line, source }];
/**
 * The least protected business risk as an answer and chip (weakestRisks, the rule of the opening's line), the one
 * wording on the Summary card and on the program tab's section (UX-09). A tie names every risk and agrees in number
 * (PL-06); all protections in place reads "Every protection in place, fully or partly" (PL-07).
 */
export function weakestCard(rows) {
    const w = weakestRisks(rows);
    if (!w)
        return { answer: 'Not measured yet', chip: { label: 'No results yet', tone: 'neutral' } };
    if (w.pct >= 100)
        return { answer: 'Every protection in place, fully or partly', chip: { label: 'All in place', tone: 'in_place' } };
    const tie = w.rows.length > 1;
    return {
        answer: joinNames(w.rows.map((r) => r.label)),
        chip: { label: tie ? `Each has ${w.pct}% of its protections in place` : `${w.pct}% of its protections in place`, tone: 'neutral' },
    };
}
/** The Summary's cards (F1), on summaryLines. */
export function summaryCards(d) {
    const lines = summaryLines(d);
    if (d.firm) {
        const f = d.firm;
        const s = f.source === null ? f.march2025Baselines : null;
        const v = s ? s.btc.pct : f.headline.btc.value;
        const kind = firmAttentionKind(f);
        const attention = kind === 'off_track'
            ? { answer: plural(f.headline.plans.offTrack, 'action plan'), chip: { label: 'Off track', tone: 'off_track' }, source: `${f.firmName}'s ${SOURCE.plans}` }
            : kind === 'no_rating'
                ? { answer: f.inCdwProgramme === false ? 'A first 2026 rating' : 'The CDW assessment', chip: { label: f.inCdwProgramme === false ? `Not in ${PROGRAM_NAME}` : 'Not complete yet', tone: 'neutral' }, source: SOURCE.tracker }
                : { ...weakestCard(f.businessRisks.map((r) => ({ label: r.label, pct: r.coveragePct }))), source: firmSourceText(f) };
        const chips = ratedChips(v, f.headline.btc.total, GOAL_SIZE.btc);
        // Every rated protection in place, with controls not rated yet, is not "All in place" (SFH-01).
        if (attention.chip.tone === 'in_place' && chips.position.tone !== 'in_place' && unratedNote(f.headline.btc.total, GOAL_SIZE.btc)) {
            attention.answer = 'Every rated protection in place, fully or partly';
            attention.chip = chips.position;
        }
        return [
            ...card('position', `How protected is ${f.firmName}?`, v === null ? 'No results yet' : s ? `${pct(v)} in March 2025` : pct(v), s ? { label: 'March 2025 survey', tone: 'neutral' } : chips.position, lines[0], firmSourceText(f)),
            ...card('goal', 'What is the goal?', GOALS.btc, s ? { label: 'No 2026 rating yet', tone: 'neutral' } : chips.goal, lines[1], SOURCE.goal),
            ...card('attention', 'What needs attention?', attention.answer, attention.chip, lines[2], attention.source),
        ];
    }
    const n = d.network;
    const wave2 = networkAttentionKind(n, d.asOf) === 'wave2';
    const attention = wave2
        ? { answer: 'BTI Wave 2', chip: wave2Chip(d.asOf), source: `${SOURCE.btiWave2}; ${networkSourceText(n)}` }
        : { ...weakestCard((n.businessRisks ?? []).map((r) => ({ label: r.label, pct: r.coveragePct }))), source: networkSourceText(n) };
    return [
        ...card('position', 'How protected is the network?', n.btcMedian === null ? 'Not measured yet' : pct(n.btcMedian), stateOf(n.btcMedian), lines[0], networkSourceText(n)),
        ...card('goal', 'What is the goal?', GOALS.btc, toGo(n.btcMedian), lines[1], SOURCE.goal),
        ...card('attention', 'What needs attention?', attention.answer, attention.chip, lines[2], attention.source),
    ];
}
export function programState(f) {
    if (f.inCdwProgramme === false || f.program?.cdwStart?.status === 'not_in_programme')
        return 'not_in_programme';
    if (!f.assessments.lastIndependent) {
        const s = f.program?.cdwStart;
        return s?.status === 'started' ? 'under_way' : s?.status === 'planned' && s.date ? 'planned' : 'not_scheduled';
    }
    if (f.findings.length)
        return 'list_received';
    return f.program?.steps?.find((x) => x.step === 'ras_report')?.status === 'complete' ? 'list_waiting' : 'report_waiting';
}
/** Each program state's chip: one wording per state, wherever the state is said. */
export const PROGRAM_STATE_CHIP = {
    // 5E pre-gate (CP-02, PL-04): the program by its one name.
    not_in_programme: () => ({ label: `Not in ${PROGRAM_NAME}`, tone: 'neutral' }),
    not_scheduled: (s) => ({ label: s?.status === 'to_be_confirmed' ? 'Dates to be confirmed' : 'Not scheduled yet', tone: 'neutral' }),
    planned: (s) => ({ label: s?.date ? `Planned for ${longDate(s.date)}` : 'Planned', tone: 'neutral' }),
    under_way: () => ({ label: 'Assessment under way', tone: 'progress' }),
    report_waiting: () => ({ label: 'Waiting for the CDW report', tone: 'neutral' }),
    list_waiting: () => ({ label: 'Report received, list to come', tone: 'progress' }),
    list_received: () => ({ label: 'Findings list received', tone: 'progress' }),
};
/** The Assessment program's cards (F1), on assessmentLines. */
export function programCards(d) {
    const lines = assessmentLines(d);
    // DEC-53: the tab opens with what CDW found; owners and due dates stay on the firms' own pages (no cards on them).
    const findingsCards = (list, question, source, network = false) => {
        const c = findingCounts(list);
        // NEXT PASS 5 (F4, DEC-55 item 6): no closed count; the chip says the open findings' state.
        // 5E (DEC-57, DEC-58 item 7): not "Open, as CDW reported them": whether a finding is still open comes from each
        // firm's reported progress, so the chip says the list is kept current by the firms. No chip repeats its answer.
        // SFH-04 (5E pre-gate): "Every finding closed" only when every finding is closed; findings the firm's leaders accepted
        // are said as accepted, and the network's count covers only the lists received.
        const where = network ? ' on the lists received' : '';
        const none = c.closed === list.length
            ? { label: `Every finding closed${where}`, tone: 'in_place' }
            : { label: `None open${where}; ${c.accepted} accepted by ${network ? "firms'" : "the firm's"} leaders`, tone: 'neutral' };
        // 5E NEXT PASS 2 (E7): a state of the open findings (how many CDW rated high or very high), not the page's date.
        return card('position', question, plural(c.open, 'open finding'), c.open ? higherState(list.filter(isOpenFinding)) : none, lines[0], source);
    };
    if (d.firm) {
        const f = d.firm;
        // One state, one wording (PL-02, PL-03): every chip on the tab is derived from programState.
        const state = programState(f);
        if (!f.assessments.lastIndependent) {
            const s = f.program?.cdwStart;
            const next = state === 'not_in_programme'
                ? { answer: 'A first 2026 rating', chip: PROGRAM_STATE_CHIP.not_in_programme(s) }
                : state === 'planned'
                    ? { answer: 'The CDW assessment', chip: PROGRAM_STATE_CHIP.planned(s) }
                    : state === 'under_way'
                        ? { answer: 'The CDW report', chip: PROGRAM_STATE_CHIP.under_way(s) }
                        : { answer: 'A date for the CDW assessment', chip: PROGRAM_STATE_CHIP.not_scheduled(s) };
            // The goal's progress, in the same state's terms: never "Not started" beside "Assessment under way" or "Planned".
            const goal = state === 'under_way' ? { label: 'Under way', tone: 'progress' } : state === 'planned' ? { label: 'Planned', tone: 'neutral' } : { label: 'Not started yet', tone: 'neutral' };
            return [
                ...card('position', `Has ${f.firmName} been assessed independently?`, 'Not yet', PROGRAM_STATE_CHIP[state](s), lines[0], SOURCE.tracker),
                ...card('goal', 'What is the goal?', 'An independent assessment and its findings list', goal, lines[1], SOURCE.tracker),
                ...card('attention', 'What needs attention?', next.answer, next.chip, lines[2], SOURCE.tracker),
            ];
        }
        if (!f.findings.length) {
            // The findings list comes with the report: until the report is in, it is the report that is awaited.
            return [
                ...card('position', `What did CDW find at ${f.firmName}?`, state === 'list_waiting' ? 'List not received yet' : 'Report not received yet', PROGRAM_STATE_CHIP[state](f.program?.cdwStart), lines[0], SOURCE.tracker),
            ];
        }
        return findingsCards(f.findings, `What did CDW find at ${f.firmName}?`, `${f.firmName}'s CDW risk register, with progress as the firm reports it`);
    }
    const n = d.network;
    const e = n.evidence ?? { cdw: n.firmsWithResults, self: 0, survey: 0, none: Math.max(0, n.firmsInNetwork - n.firmsWithResults) };
    const total = memberFirms(n);
    // PL-08: no bare zero; with every firm reported on, it says so.
    const reported = total - e.cdw > 0 ? { label: `${total - e.cdw} still to come`, tone: 'neutral' } : { label: 'All reported', tone: 'in_place' };
    if (!n.findings || !n.findings.length) {
        return [
            ...card('position', 'How many firms has CDW reported on?', `${e.cdw} of ${total}`, reported, lines[0], SOURCE.tracker),
        ];
    }
    const lists = new Set(n.findings.map((x) => x.firmCode)).size;
    return findingsCards(n.findings, 'What did CDW find across the network?', `${SOURCE.register}; ${lists === 1 ? "1 firm's list" : `${lists} firms' lists`} received`, true);
}
/** The network tab's cards (F1), on networkLines; empty when fewer than two firms have results. */
export function networkCards(n, selected, asOf) {
    const lines = networkLines(n, selected, asOf) ?? [];
    if (!lines.length)
        return [];
    const vals = rankedDots(n.distribution).ranked.map((x) => x.pct).sort((a, b) => b - a);
    const me = n.distribution.find((x) => Boolean(x.isYou || (selected !== null && x.firmCode === selected)));
    const below = belowHalf(n);
    const apart = Boolean(me && me.isYou && me.source !== undefined && me.source !== 'cdw');
    const third = me && me.pct !== null
        ? {
            question: `Where is ${me.firmName ?? 'your firm'}?`,
            answer: apart ? pct(me.pct) : `${ordinalOf(vals.indexOf(me.pct) + 1)} of ${vals.length}`,
            chip: apart ? { label: me.source === 'self' ? 'Self-assessed, not ranked' : 'Not from CDW, not ranked', tone: 'neutral' } : stateOf(me.pct),
        }
        : below.count
            ? { question: 'What needs attention?', answer: below.names ? joinNames(below.names) : plural(below.count, 'firm'), chip: { label: 'Less than half in place', tone: 'neutral' } }
            : { question: 'What needs attention?', answer: 'No firm below half', chip: { label: 'Every firm above half', tone: 'progress' } };
    return [
        ...card('position', 'How far apart are the firms?', `${pct(vals.at(-1))} to ${pct(vals[0])}`, { label: `${vals.length} of ${n.firmsInNetwork} firms with CDW results`, tone: 'neutral' }, lines[0], SOURCE.cdw),
        ...(n.btcMedian === null ? [] : card('goal', 'How far is the typical firm from the goal?', pct(n.btcMedian), toGo(n.btcMedian), lines[1], `${networkSourceText(n)}; ${SOURCE.goal}`)),
        ...card('attention', third.question, third.answer, third.chip, lines[n.btcMedian === null ? 1 : 2], SOURCE.cdw),
    ];
}
/** The BTI tab's cards (F1), on btiLines. */
export function btiCards(d) {
    const lines = btiLines(d);
    const due = longDate(BTI_WAVE2_DUE);
    const size = BTI_WAVE2_ITEMS.length;
    if (d.firm) {
        const f = d.firm;
        const s = f.source === null ? f.march2025Baselines : null;
        const w2 = f.headline.bti.wave2;
        const v = s ? s.btiWave2.pct : w2.value;
        const notIn = w2.total - w2.inPlace;
        const unrated = Math.max(0, size - w2.total);
        const left = s || w2.value === null
            ? 'Wave 2 by its due date'
            : notIn
                ? `${notIn} of ${w2.total} not in place yet`
                : w2.partly
                    ? `${w2.partly} of ${w2.total} only partly in place`
                    : unrated
                        ? `${unrated} of ${size} not assessed yet`
                        : 'Every requirement fully in place';
        const chips = ratedChips(v, w2.total, size);
        return [
            ...card('position', `How ready is ${f.firmName} for Wave 2?`, v === null ? 'No results yet' : s ? `${pct(v)} in March 2025` : pct(v), s ? { label: 'March 2025 survey', tone: 'neutral' } : chips.position, lines[0], firmSourceText(f)),
            ...card('goal', 'What is the goal?', `All ${size} requirements by ${due}`, s ? { label: 'No 2026 rating yet', tone: 'neutral' } : chips.goal, lines[1], SOURCE.btiWave2),
            ...card('attention', 'What needs attention?', left, wave2Chip(d.asOf), lines[2], `${SOURCE.btiWave2}; ${firmSourceText(f)}`),
        ];
    }
    const n = d.network;
    const c = btiVerdictCounts();
    const w2 = n.btiPositions?.filter((p) => p.wave === 2);
    const recorded = n.btiWave2 && w2 ? size - btiPositionCounts(w2, 2).not_set : null;
    return [
        ...card('position', 'How did BTI rate Wave 1?', `${c.satisfied} of ${BTI_WAVE1_EVALUATION.items.length} satisfied`, { label: 'One review so far', tone: 'neutral' }, lines[0], SOURCE.btiReview),
        ...card('goal', 'How ready is the network for Wave 2?', n.btiWave2Median === null ? 'No figure yet' : pct(n.btiWave2Median), stateOf(n.btiWave2Median), lines[1], networkSourceText(n)),
        ...card('attention', 'What needs attention?', `Wave 2, due ${due}`, wave2Chip(d.asOf), lines[2], recorded === null ? SOURCE.btiWave2 : `${SOURCE.btiWave2}; ${SOURCE.nationalIt}`),
    ];
}
/** One firm's own position as a count (a firm member's BTI tab), so the area sums are the one rule below. */
export const ownCount = (r) => ({
    controlId: r.controlId,
    rated: r.status === 'fully' || r.status === 'partly' || r.status === 'not_yet' ? 1 : 0,
    inPlace: r.status === 'fully' || r.status === 'partly' ? 1 : 0,
    partly: r.status === 'partly' ? 1 : 0,
});
/**
 * 5D (F6): each BTI area of a wave, with its requirements and the firm-requirements fully, partly and not yet in place
 * summed over them, the same for Wave 1 and Wave 2, on the page and in the PDF. Counts are the API's per-requirement
 * counts (the firms with CDW results), or a firm's own positions (ownCount).
 */
export function btiAreaCounts(wave, counts) {
    const items = wave === 1 ? BTI_WAVE1_EVALUATION.items : BTI_WAVE2_ITEMS;
    const domains = [...new Set(items.map((i) => i.domain))];
    return domains.map((domain) => {
        const inArea = items.filter((i) => i.domain === domain);
        const sum = { fully: 0, partly: 0, notYet: 0 };
        for (const i of inArea) {
            const m = counts.find((x) => x.controlId === i.controlId);
            if (!m)
                continue;
            sum.fully += m.inPlace - m.partly;
            sum.partly += m.partly;
            sum.notYet += m.rated - m.inPlace;
        }
        return { domain, requirements: inArea.length, ...sum };
    });
}
/**
 * Why there are no focus points (SFH-09), true to the data: too few firms with results, or nothing stands out.
 */
export const focusEmptyReason = (n) => n.firmsWithResults < 5
    ? 'Focus points appear once five or more firms have CDW results.'
    : 'Nothing stands out across the network at present: no gap most firms share, no control left partly done, and no business risk far behind the rest.';
/**
 * Why fewer than FOCUS_MIN points stand out (DEC-56), or null when there are none or enough: each rule that did not
 * fire, with its own cause in the data. Never padded: the reason is said instead.
 */
export function focusShortReason(d, points) {
    if (!points.length || points.length >= FOCUS_MIN)
        return null;
    const why = focusMisses(d);
    return `Only ${points.length === 1 ? 'one point stands' : 'two points stand'} out today: ${joinNames(why)}.`;
}
/** The share of rated firms at which a shared control "stands out" (DEC-56): at least half of them. */
export const STANDS_OUT_SHARE = 0.5;
/** The weakest business risk "stands out" when less than this share of its protections is in place at the typical firm. */
export const WEAKEST_RISK_BELOW = 50;
/** Each focus rule that did not fire, with its own cause in the data (DEC-56), in the rules' order. */
export function focusMisses(d) {
    const n = d.network;
    const out = [];
    if (daysToWave2(d.asOf) < 0)
        out.push('BTI Wave 2 is past its due date');
    else if (n.btiWave2Median !== null && n.btiWave2Median >= 100)
        out.push('the typical firm has every BTI Wave 2 requirement in place');
    if (!n.topGaps.some(sharedGap))
        out.push(n.topGaps.length ? 'no gap is shared by half the firms that rated it' : 'no gap results yet');
    // A firm member is not sent the business risks or the program summary: nothing is said about rules it cannot see.
    const w = n.businessRisks ? weakestRisks(n.businessRisks.map((x) => ({ label: x.label, pct: x.coveragePct }))) : undefined;
    if (n.businessRisks && !w)
        out.push('no business-risk figures yet');
    else if (w && w.pct >= WEAKEST_RISK_BELOW)
        out.push('every business risk has at least half its protections in place at the typical firm');
    if (!n.toStrengthen.some(sharedPartly))
        out.push('no control is only partly in place at half the firms that rated it');
    if (n.programSummary && !programWaiting(n))
        out.push("every member firm's CDW assessment is scheduled");
    return out;
}
const sharedGap = (g) => g.firmsRated > 0 && g.firmsNotYet / g.firmsRated >= STANDS_OUT_SHARE;
const sharedPartly = (g) => g.firmsRated > 0 && g.firmsPartly / g.firmsRated >= STANDS_OUT_SHARE;
const programWaiting = (n) => (n.programSummary ? n.programSummary.notScheduled + n.programSummary.toBeConfirmed : 0);
/** The fewest and most focus points the Summary shows (F3). */
export const FOCUS_MIN = 3;
export const FOCUS_MAX = 5;
/** A control's plain summary as a bullet's lead: no closing stop. */
const asLead = (s) => s.replace(/\.$/, '');
/**
 * Where member firms can focus to improve the network's security (F3, DEC-49), generated from the network data the
 * reader may see, in this order:
 *  1. wave2: BTI Wave 2 while its due date is ahead and the typical firm has not every requirement in place, with the
 *     BTI area with the least in place across the firms with CDW results (National and Board);
 *  2. common_gap: up to two controls not yet in place at at least half the firms that rated them (topGaps);
 *  3. weakest_risk: the least protected business risk, when less than half its protections are in place at the
 *     typical firm (National and Board);
 *  4. to_strengthen: the control only partly in place at the most firms, when that is at least half the firms that
 *     rated it;
 *  5. program: any member firm not scheduled yet or with dates to be confirmed.
 * DEC-56: each rule depends only on its own data and threshold; the points are ranked in this order and at most five
 * are shown. None is ever added to reach a count; fewer than three come with focusShortReason.
 */
export function focusPoints(d) {
    const n = d.network;
    const out = [];
    const days = daysToWave2(d.asOf);
    if (days >= 0 && (n.btiWave2Median === null || n.btiWave2Median < 100)) {
        const areas = n.btiWave2Areas?.areas.filter((a) => a.fully + a.partly + a.notYet > 0) ?? [];
        const share = (a) => (a.fully + a.partly) / (a.fully + a.partly + a.notYet);
        const least = areas.length ? [...areas].sort((a, b) => share(a) - share(b))[0] : null;
        const where = n.btiWave2Median === null ? '' : `; the typical firm has ${pct(n.btiWave2Median)} of its ${size2()} requirements in place`;
        out.push({
            rule: 'wave2',
            lead: `Finish BTI Wave 2 by ${longDate(BTI_WAVE2_DUE)}`,
            text: `${days > 0 ? `${plural(days, 'day')} left` : 'Due today'}${where}${least ? `, and ${least.domain} has the least in place across firms` : ''}.`,
            source: `${SOURCE.btiWave2}; ${SOURCE.cdw}`,
        });
    }
    for (const g of n.topGaps.filter(sharedGap).slice(0, 2)) {
        out.push({ rule: 'common_gap', lead: asLead(g.label), text: `Not yet in place at ${g.firmsNotYet} of the ${g.firmsRated} firms that rated it in their CDW assessment.`, source: SOURCE.cdw });
    }
    const w = weakestRisks((n.businessRisks ?? []).map((r) => ({ label: r.label, pct: r.coveragePct })));
    if (w && w.pct < WEAKEST_RISK_BELOW) {
        const names = joinNames(w.rows.map((r) => r.label.toLowerCase()));
        // A tie agrees in number (PL-06): "against each of them", as the card says "Each has".
        const against = w.rows.length > 1 ? 'each of them' : 'it';
        out.push({ rule: 'weakest_risk', lead: `Strengthen protection against ${names}`, text: `${pct(w.pct)} of the protections against ${against} are in place at the typical firm, the least of the four business risks.`, source: SOURCE.cdw });
    }
    const s = n.toStrengthen.find(sharedPartly);
    if (s) {
        out.push({ rule: 'to_strengthen', lead: `Complete the work to ${lowerFirst(asLead(s.label))}`, text: `Only partly in place at ${s.firmsPartly} of the ${s.firmsRated} firms that rated it in their CDW assessment.`, source: SOURCE.cdw });
    }
    const waiting = programWaiting(n);
    if (waiting) {
        out.push({ rule: 'program', lead: 'Schedule the remaining CDW assessments', text: `${plural(waiting, 'member firm')} ${waiting === 1 ? 'is' : 'are'} not scheduled yet or ${waiting === 1 ? 'has' : 'have'} dates to be confirmed.`, source: SOURCE.tracker });
    }
    return out.slice(0, FOCUS_MAX);
}
const size2 = () => BTI_WAVE2_ITEMS.length;
const lowerFirst = (t) => (/^[A-Z][a-z]/.test(t) ? `${t[0].toLowerCase()}${t.slice(1)}` : t);
//# sourceMappingURL=leadership-cards.js.map